/**
 * <product-tabs> (sections/featured-products-hover.liquid, home "The bestsellers" row): tabs over product rows, after
 * bellroy.com — the row's own products open first; "Recently viewed" (this browser's last product pages, localStorage
 * `harber:recent`, written by product.js) is filled at once from cards cached in this browser, the rest fetched from the product's collection card
 * (?view=finder-card); its tab shows as soon as there is a history (inline script in the section). "View all" follows the open tab.
 */
(() => {
  if (customElements.get('product-tabs')) return;
  const T = window.Theme || {};
  const root = (T.routes?.root || '/').replace(/\/$/, '');

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((t) => t.addEventListener('click', () => this.select(t)));
      this.querySelector('[role="tablist"]').addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        const shown = this.tabs.filter((t) => !t.hidden);
        const i = shown.indexOf(document.activeElement);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? shown.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + shown.length) % shown.length;
        e.preventDefault();
        shown[next].focus();
        this.select(shown[next]);
      });
      const recent = this.querySelector('[data-recent]');
      if (recent) this.loadRecent(recent); // cached cards at once, the rest fetched now (not when the row scrolls in)
    }

    select(tab) {
      for (const t of this.tabs) {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = this.querySelector(`[data-panel="${t.dataset.tab}"]`);
        if (panel) panel.hidden = !on;
        if (on && panel) {
          const all = this.querySelector('[data-view-all]');
          if (all) { all.hidden = !panel.dataset.href; if (panel.dataset.href) all.href = panel.dataset.href; }
          panel.querySelector('scroll-carousel')?.update?.(); // arrows of a panel that was hidden
          const track = panel.querySelector('[data-track]');
          if (track) track.scrollLeft = 0;
        }
      }
    }

    /* Cards are cached in this browser (localStorage `harber:recent-cards`, handle → card HTML, 6 h) so the tab fills at
       once; missing or older ones are fetched in the background and the row redrawn. */
    async loadRecent(list) {
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem('harber:recent') || '[]'); } catch { /* private mode */ }
      handles = handles.filter((h) => typeof h === 'string' && /^[a-z0-9-]+$/.test(h)).slice(0, Number(this.dataset.limit) || 8);
      const tab = this.querySelector('[data-tab="recent"]');
      if (!handles.length) { if (tab) tab.hidden = true; return; }
      const KEY = 'harber:recent-cards', TTL = 6 * 3600e3;
      let cache = {};
      try { cache = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { /* */ }
      const lang = document.documentElement.lang || '';
      const fresh = (h) => cache[h] && cache[h].l === lang && Date.now() - cache[h].t < TTL;
      const toLi = (html) => {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const li = document.createElement('li');
        li.className = 'prow__item';
        li.append(tpl.content);
        return li;
      };
      const draw = () => {
        const items = handles.filter((h) => cache[h]?.html).map((h) => toLi(cache[h].html));
        if (items.length) list.replaceChildren(...items);
        if (tab) tab.hidden = !items.length && handles.every((h) => cache[h]);
      };
      draw();
      const stale = handles.filter((h) => !fresh(h));
      if (!stale.length) return;
      await Promise.all(stale.map((h) => fetch(`${root}/products/${encodeURIComponent(h)}?view=finder-card`)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const card = new DOMParser().parseFromString(html, 'text/html').querySelector('li');
          cache[h] = { html: card ? card.innerHTML : '', t: Date.now(), l: lang };
        })
        .catch(() => {})));
      for (const h of Object.keys(cache)) if (!handles.includes(h)) delete cache[h];
      try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* full */ }
      draw();
    }
  }
  if (!customElements.get('product-tabs')) customElements.define('product-tabs', ProductTabs);
})();
